import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Layout } from './Layout';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';
import { PaginationCustom } from './ui/pagination-custom';
import {
  Home,
  ArrowLeft,
  Search,
  Filter,
  Eye,
  Edit3,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Calendar,
  User,
  FileSearch,
  ChevronRight,
  ChevronDown,
  Target,
  ThumbsUp,
  ThumbsDown,
  SkipForward,
  Download,
  Upload,
  Settings,
  Activity,
  TrendingUp,
  Users,
  MessageSquare,
  Flag,
  CheckCircle2,
  XCircle,
  Pause,
  Star,
  Shield,
  Zap,
  Heart,
  Brain,
  Pill,
  Stethoscope,
  ClipboardCheck,
  TrendingDown,
  HelpCircle,
  AlertCircle,
  Info,
  UserCheck,
  Calendar as CalendarIcon,
  Undo,
  Edit,
  CheckSquare,
  Minus,
  ChevronLeft,
  Square
} from 'lucide-react';
import { toast } from 'sonner';
import { AppContext } from '../src/App';
import { useAuth } from './AuthContext';
import { cn } from './ui/utils';
import kantime from '../src/assets/kanTime.png';
import AI from '../src/assets/ailogo.png';
import { apiClient } from '../src/Utils/apiClient';
import { ac } from 'vitest/dist/chunks/reporters.nr4dxCkA.js';
import { DialogClose, DialogTrigger } from '@radix-ui/react-dialog';
import { set } from 'date-fns';
import { RNChartRecord, Section, Widget } from '@/types/rnChart';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '@/routes/config';


// Generate comprehensive healthcare audit goals data with sub-goals
const generateHealthcareAuditGoals = (auditData: any) => {
  const sections = ['70-1A', '70-1B', '70-1C', '70-2A', '70-2B', '70-3A', '70-3B', '70-4A', '70-4B', '70-5A'];

  const mainGoalTasks = [
    'By December 2024, [Client] will perform goal 1, 50% of the time',
    'By March 2025, [Client] will perform goal 1, 75% of the time',
    'By June 2025, [Client] will demonstrate self-regulation skills in 80% of observed situations',
    'Improve communication skills using AAC device with 90% accuracy',
    'Increase independent living skills performance by 60% within 6 months',
    'Develop social interaction skills with peers during structured activities',
    'Maintain medication compliance with 95% adherence rate',
    'Complete daily living activities with minimal supervision 70% of the time',
    'Demonstrate safety awareness in community settings 85% of occasions',
    'Participate in vocational training activities with 80% attendance'
  ];

  // Sub-goals that appear when expanded
  const subGoalTasks = [
    'Document in the notes section (0–4 scale, 0=Refusal ... 4=100%)',
    'Interventions: List in notes section (HOH, M, V, I)',
    'Was there Parental Involvement?',
    'Track weekly progress measurements and documentation',
    'Assess environmental modifications and supports needed',
    'Monitor family engagement and participation levels',
    'Document baseline measurements and target criteria',
    'Evaluate intervention effectiveness and modifications needed',
    'Review safety protocols and incident reporting'
  ];

  // Updated actions - only "Performed" and "Declined"
  const actions = ['Performed', 'Declined'];
  const reasonCodes = ['1', '2', '3', '4', '5', null];
  const specialReasonCodes = ['V + M + HOH', 'HOH + M', 'V + I', 'M + V'];

  const notes = [
    'Client performed goal 1 without much support',
    'Guardian declined to participate.',
    'Client showed significant improvement in task completion',
    '4',
    '3',
    '2',
    '1',
    '0',
    'V + M + HOH',
    'Client required additional prompting and guidance',
    'Excellent participation with minimal assistance needed',
    'Some resistance noted but overall compliance maintained',
    'Progress documented with detailed behavioral observations'
  ];

  // Updated decisions - only "Compliant" and "Non-Compliant"
  const decisions = ['Compliant', 'Non-Compliant'];
  const suggestions = [
    'Clear description of the client behaviour',
    'Documented progress must be 0, 1, 2, 3, or 4',
    '',
    'Continue current intervention strategies with increased frequency',
    'Recommend additional family involvement in goal activities',
    'Consider modification of goal criteria based on client progress',
    'Implement visual supports to enhance understanding',
    'Schedule additional training sessions for skill development',
    'Monitor progress more frequently during initial implementation'
  ];

  const data = [];
  const goalCount = auditData?.goalCount || Math.floor(Math.random() * 10) + 8; // 8-18 goals

  for (let i = 0; i < goalCount; i++) {
    const section = sections[Math.floor(Math.random() * sections.length)];
    const action = actions[Math.floor(Math.random() * actions.length)];
    const decision = decisions[Math.floor(Math.random() * decisions.length)];
    const confidence = Math.floor(Math.random() * 5) + 96; // 96-100% for healthcare compliance
    const reasonCode = reasonCodes[Math.floor(Math.random() * reasonCodes.length)];

    // Main goal task
    const mainGoalTask: any = mainGoalTasks[Math.floor(Math.random() * mainGoalTasks.length)];
    const collapsedGoalTask = mainGoalTask.length > 60
      ? mainGoalTask.substring(0, 60) + '...'
      : mainGoalTask;

    const mainNotes: any = notes[Math.floor(Math.random() * notes.length)];
    const collapsedNotes = mainNotes.length > 40
      ? mainNotes.substring(0, 40) + '...'
      : mainNotes;

    const mainSuggestion: any = suggestions[Math.floor(Math.random() * suggestions.length)];
    const collapsedSuggestion = mainSuggestion.length > 50
      ? mainSuggestion.substring(0, 50) + '...'
      : mainSuggestion;

    // Generate 2-4 sub-goals for each main goal
    const subGoalCount = Math.floor(Math.random() * 3) + 2; // 2-4 sub-goals
    const subGoals = [];

    for (let j = 0; j < subGoalCount; j++) {
      const subAction = actions[Math.floor(Math.random() * actions.length)];
      const subDecision = decisions[Math.floor(Math.random() * decisions.length)];
      const subConfidence = Math.floor(Math.random() * 5) + 96; // 96-100%
      const subReasonCode = j === 2 ? reasonCodes[Math.floor(Math.random() * reasonCodes.length)] : null;

      let subNotes: any = '';
      let subSuggestion: any = '';

      // Set specific notes and suggestions based on sub-goal type
      if (j === 0) {
        // Documentation sub-goal
        subNotes = ['4', '3', '2', '1', '0'][Math.floor(Math.random() * 5)];
        subSuggestion = '';
      } else if (j === 1) {
        // Interventions sub-goal
        subNotes = specialReasonCodes[Math.floor(Math.random() * specialReasonCodes.length)];
        subSuggestion = '';
      } else {
        // Other sub-goals
        subNotes = notes[Math.floor(Math.random() * notes.length)];
        subSuggestion = suggestions[Math.floor(Math.random() * suggestions.length)];
      }

      subGoals.push({
        goalTask: subGoalTasks[Math.floor(Math.random() * subGoalTasks.length)],
        action: subAction,
        reasonCode: subReasonCode,
        notes: subNotes,
        decision: subDecision,
        confidenceScore: subConfidence,
        suggestion: subSuggestion
      });
    }

    data.push({
      id: `goal-${i + 1}`,
      section,
      goalTask: {
        full: mainGoalTask,
        collapsed: collapsedGoalTask
      },
      action,
      reasonCode,
      notes: {
        full: mainNotes,
        collapsed: collapsedNotes
      },
      decision,
      confidenceScore: confidence,
      suggestion: {
        full: mainSuggestion,
        collapsed: collapsedSuggestion
      },
      subGoals, // Add sub-goals to the main goal
      isExpanded: false,
      priority: decision === 'Non-Compliant' ? 'High' : 'Low',
      lastReviewed: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      reviewTime: (Math.random() * 2 + 0.5).toFixed(1),
      category: ['Clinical Documentation', 'Patient Safety', 'Quality Measures', 'Care Coordination'][Math.floor(Math.random() * 4)],
      needsAction: decision === 'Non-Compliant',
    });
  }

  return data.sort((a: any, b: any) => {
    // Sort by section first, then by priority
    const sectionCompare = a.section.localeCompare(b.section);
    if (sectionCompare !== 0) return sectionCompare;

    const priorityOrder: any = { 'High': 2, 'Low': 1 };
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });
};
const transformAuditResponse = (apiData: any): RNChartRecord => {

  // Recursive widget mapper
  const mapWidget = (w: any): Widget => {

    // ---- TABLE WIDGET ----
    if (w.type === "table") {
      return {
        widget_type: "table",
        label: w.label,
        columns: w.columns?.map((col: any) => ({
          label: col.label,
          field_name: col.key
        })) || [],
        rows: w.rows?.map((r: any) => ({
          row_id: r.row_id,
          ...r.values
        })) || []
      };
    }

    // ---- GROUP WIDGET ----
    if (w.type === "group" && w.items) {
      return {
        widget_type: "group",
        group_name: w.label,
        fields: w.items.map(mapWidget)
      };
    }

    // ---- FIELD WIDGET ----
    let displayValue = w.value;

    // Checkbox conversion
    if (w.datatype === "checkbox") {
      displayValue =
        w.value === true ? "true" :
          w.value === false ? "false" :
            null; // Preserve null/undefined for checkboxes too
    }

    return {
      widget_type: "field",
      label: w.label,
      field_name: w.key,
      value: displayValue ?? null, // Preserve null/empty values from backend
      datatype: w.datatype,
      enabled: w.enabled
    };
  };

  // ---- MAP SECTIONS ----
  const rawSections = apiData.ai_metadata?.content?.sections || [];

  const mappedSections: Section[] = rawSections.map((sec: any, idx: number) => ({
    section_number: idx + 1,
    section_title: sec.title,
    widgets: sec.widgets?.map(mapWidget) || [],
    adjudication: sec.adjudication,
    not_applicable: sec.not_applicable,
    noteToClinician: sec.noteToClinician || sec.NoteToClinician || undefined
  }));

  // ---- AI Decision ----
  const decision = apiData.ai_metadata?.content?.OverallAuditDecision || "";
  const aiDecision = decision.toLowerCase().includes("approv")
    ? "approve"
    : "review";

  return {
    id: apiData.id,
    chartId: apiData.chart_id,
    patientName: apiData.client_name,
    clinicianName: apiData.clinician_name,
    visitDate: apiData.visit_date,
    promptName: apiData.prompt_name,
    promptVersion: apiData.prompt_version,

    modelName: apiData.model_type || apiData.ai_metadata?.model || "Unknown",
    modelProvider: apiData.ai_metadata?.provider || "Unknown",

    aiDecision,
    aiReason: apiData.ai_metadata?.content?.overallAuditFeedback || "No reasoning provided.",

    visitStart: apiData.chart_data?.visit?.checkInTime,
    visitEnd: apiData.chart_data?.visit?.checkOutTime,
    visitDuration: apiData.chart_data?.visit?.hours,

    chartData: {
      sections: mappedSections
    }
  };
};

interface GoalAction {
  goalId: string;
  action: string;
  comment: string;
}

interface AuditStatus {
  status: 'pending' | 'approved' | 'rejected';
  approvedBy?: string;
  approvedByRole?: string;
  approvedAt?: Date;
  comment?: string | undefined;
}

// Mock current user - in real app this would come from auth context
const getCurrentUser = () => ({
  name: 'Dr. Emily Martinez',
  role: 'Senior Clinical Supervisor',
  id: 'USR-2024-001',
  department: 'Quality Assurance'
});

export function AuditGoalsDetailView() {
  const { user, token } = useAuth();
  const { selectedAuditData, sideBarItems, previousViewScreen, setCurrentView, currentIndexOfListedChart, setSelectedAuditData, setCurrentIndexOfListedChart,
    currentIndexOfClinicianChart,
    setCurrentIndexOfClinicianChart,
    orgConfig } = React.useContext(AppContext);
  const location = useLocation();
  const passedAuditData = location.state?.auditData;
  const passedPreviousScreen = location.state?.previousScreen;
  const passedCurrentchartIndex = location.state?.currentchartIndex;
  const auditId = location.state?.auditId;
  const [goalsData, setGoalsData] = useState(() => generateHealthcareAuditGoals(selectedAuditData));
  const [selectedGoal, setSelectedGoal] = useState<any>(null);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [showBulkDialog, setBulkDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showAuditActionDialog, setShowAuditActionDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<GoalAction | null>(null);
  const [pendingAuditAction, setPendingAuditAction] = useState<'approve' | 'reject' | null>(null);
  const [actionComment, setActionComment] = useState('');
  const [auditActionComment, setAuditActionComment] = useState('');
  const [bulkAction, setBulkAction] = useState<'approve' | 'reject' | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [expandAll, setExpandAll] = useState(false);
  const [auditStatus, setAuditStatus] = useState<AuditStatus>({ status: 'pending' });
  const [harvestRuns, setHarvestRuns] = useState<any>([])
  const [chartData, setChartData] = useState<any>([])
  const [selectedId, setSelectedId] = useState<any>([])
  const [isAllExpanded, setIsAllExpanded] = useState(true);
  const [collapsedItems, setCollapsedItems] = useState<Set<number>>(new Set());
  const [checked, setChecked] = useState<any>()
  const [isAideChart, setIsAideChart] = useState<String>("");
  const [filters, setFilters] = useState({
    section: 'all',
    action: 'all',
    decision: 'all',
    category: 'all',
    searchQuery: '',
  });

  const navigate = useNavigate();

  // Dependency array depends on your logic, usually [selectedAuditData, passedAuditData]
  useEffect(() => {
    if (!selectedAuditData && passedAuditData) {
      setSelectedAuditData(passedAuditData);
      fetchCurrentChart();
    }
  }, []);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [showScrollText, setShowScrollText] = useState(false);
  const [selectedUndoCharts, setSelectedUndoCharts] = useState<any>(null);
  const [showNextOrPrev, setShowNextOrPrev] = useState({
    hasPrev: false,
    hasNext: false
  });
  const [totalCount, setTotalCount] = useState(0);
  const [auditDataloadng, setAuditDataLoading] = useState(false);


  // const hasAIDEcharts = selectedAuditData?.section === "AIDE";

  const isFromHarvestRun = (previousViewScreen || passedPreviousScreen) === 'harvest-run-detail';
  const activeChartIndexState = isFromHarvestRun
    ? currentIndexOfListedChart
    : currentIndexOfClinicianChart;

  const setActiveChartIndexState = isFromHarvestRun
    ? setCurrentIndexOfListedChart
    : setCurrentIndexOfClinicianChart;
  const parentViewName = isFromHarvestRun
    ? 'harvest-run-detail'
    : 'client-clinician-view';
  const chartReviewMenu = sideBarItems.find((item: any) => item.name === "Chart Review");

  const hasPermission = (permission: string) => {
    return chartReviewMenu?.userPermissions?.includes(permission);
  }

  // const hasPrev = currentIndexOfListedChart && currentIndexOfListedChart > 0;
  //   const hasNext = currentIndexOfListedChart && currentIndexOfListedChart < data.length - 1;

  useEffect(() => {
    const container = document.querySelector('main.flex-1.overflow-auto');
    if (!container) return;

    const handleScroll = () => {
      setShowScrollText(container.scrollTop > 150);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);


  const isAdmin = hasPermission("chart:create")
  const currentUser = getCurrentUser();

  const fetchData = async () => {
    setAuditDataLoading(true);
    try {
      if (!selectedAuditData?.id) {
        console.warn("fetchData called without a selectedAuditData.id. Aborting.");
        return;
      }
      const response = await fetch(`${import.meta.env.VITE_API_URL}audits/${selectedAuditData?.id}`, {


        method: 'GET',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }
      // const newData = await generateMockHarvestData(data)
      setHarvestRuns(data?.data?.audit)
      setChartData(data?.data?.audit?.ai_metadata?.content?.goalUnits)
      setIsAideChart(data?.data?.audit?.chart_data?.visit?.section)
      const transformed = transformAuditResponse(data.data.audit);
      setChart(transformed);
      return data;
    } catch (error: any) {
      console.error('Fetch error:', error.message);
      return null;
    }
    finally {
      setAuditDataLoading(false);
    }
  };

  useEffect(() => {
    fetchData()
  }, [selectedAuditData])

  // If no audit data is selected, redirect back
  React.useEffect(() => {
    if (!selectedAuditData) {
      setCurrentView('batch-approval');
    }
  }, [selectedAuditData, setCurrentView]);
  // Move to next and prev chart 

  const fetchCurrentChart = async () => {
    if (activeChartIndexState.filterItems.length > 0) {
      const total = isFromHarvestRun ? activeChartIndexState.filterItems.length : activeChartIndexState.totalChart;
      setTotalCount(total);
      setShowNextOrPrev({
        hasPrev: activeChartIndexState.index > 0,
        hasNext:
          activeChartIndexState.index <
          activeChartIndexState.filterItems.length - 1,
      });

      const data = activeChartIndexState.filterItems[activeChartIndexState.index];
      setSelectedAuditData({ ...selectedAuditData, id: data?.id });
    }
    else {
      if (isFromHarvestRun) {
        try {
          const response = await apiClient(`harvest/runs/${selectedAuditData?.run_id || auditId}`, {
            method: 'GET',
            token
          })
          const result = await response.json();
          setTotalCount(result?.data?.harvestRun?.audits?.length);
          setShowNextOrPrev({
            hasPrev: currentIndexOfListedChart.index > 0,
            hasNext: currentIndexOfListedChart.index < result?.data?.harvestRun?.audits?.length - 1
          })

          let data = result?.data?.harvestRun?.audits[currentIndexOfListedChart.index];
          setSelectedAuditData({ ...selectedAuditData, id: data?.id })
        } catch (e) {
          console.error("Failed to fetch harvest run list:", e);
        }

      } else {
        const LIMIT = 15;
        const currentPageIndex = activeChartIndexState.currentPage || passedCurrentchartIndex.currentPage;
        const currentIndex = activeChartIndexState.index || passedCurrentchartIndex.index;
        try {
          const response = await apiClient(`audits/all?page=${currentPageIndex}&limit=${LIMIT}`, { // No filters, no pagination
            method: 'GET',
            token
          });
          const result = await response.json();
          const audits = result?.data?.audits || []; // The response has { data: { audits: [...] } }
          setTotalCount(result?.data?.pagination?.totalCount);
          const totalServerCount = result?.data?.pagination?.totalCount || 0;
          const currentGlobalIndex = ((currentPageIndex - 1) * LIMIT) + currentIndex;

          setShowNextOrPrev({
            hasPrev: currentGlobalIndex > 0,
            hasNext: currentGlobalIndex < totalServerCount - 1
          });

          let data = audits[currentIndex];
          setSelectedAuditData({ ...selectedAuditData, id: data?.id })
        } catch (e) {
          console.error("Failed to fetch all audits list:", e);
        }
      }
    }


  }
  useEffect(() => {
    fetchCurrentChart()
  }, [activeChartIndexState.index, activeChartIndexState.currentPage])

  // const handlePreviousChart=()=>{
  //   if(!showNextOrPrev.hasPrev)
  //   {
  //     setCurrentView('harvest-run-detail');
  //   }
  // setCurrentIndexOfListedChart((prev:number)=>prev-1)
  // }
  // const handleNextChart=()=>{
  //   if(!showNextOrPrev.hasNext)
  //   {
  //     setCurrentView('harvest-run-detail');
  //   }
  // setCurrentIndexOfListedChart((prev:number)=>prev+1)


  // }



  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [editNoteValue, setEditNoteValue] = useState("");

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const rawNote =
    harvestRuns?.ai_metadata?.content?.auditorNotesToClinician ??
    harvestRuns?.ai_metadata?.content?.SummaryOfNoteToClinician ??
    harvestRuns?.ai_metadata?.content?.ConcatenatedNoteToClinician ??
    harvestRuns?.ai_metadata?.content?.concatenatedNoteToClinician ??
    "";

  // Final value shown in UI
  const displayNote = removeDuplicateSentences(rawNote);
  useEffect(() => {
    if (isEditDialogOpen) {
      setEditNoteValue(displayNote);
    }
  }, [isEditDialogOpen, displayNote]);

  useEffect(() => {
    if (isEditDialogOpen && textareaRef.current) {
      const input = textareaRef.current;
      input.focus();
      const len = input.value.length;
      input.setSelectionRange(len, len);
    }
  }, [isEditDialogOpen]);

  // Mock API function - Replace with your actual fetch/axios call
  const updateNoteApi = async (newNoteText: String, auditId: String) => {
    setIsSavingNote(true);
    try {
      if (!newNoteText.trim()) {
        toast.error("Note cannot be empty", {
          description: "Please enter some text before saving.",
        });
        return;
      }

      if (newNoteText.trim() === displayNote.trim()) {
        toast.error("No changes detected", {
          description: "Modify the note before saving.",
        });
        return;
      }
      const response = await apiClient(`audits/clinician/notes/${auditId}`, {
        method: 'PUT',
        token,
        body: { auditorNotesToClinician: newNoteText }
      }
      )
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Request failed");
      setIsEditDialogOpen(false);
      fetchData();
      toast.success(data.message || "Note updated successfully");
    } catch (err: any) {
      toast.error(err.message || 'Please try again later.');
    } finally {
      setIsSavingNote(false);
    }
  };


  // Filter and paginate data
  const handlePreviousChart = () => {
    if (!showNextOrPrev.hasPrev) {
      setCurrentView(parentViewName);
      return;
    }

    if (isFromHarvestRun) {
      setActiveChartIndexState((prev: { index: number }) => ({
        ...prev,
        index: prev.index - 1,
      }));
    } else {
      const LIMIT = 15;
      const { index, currentPage } = activeChartIndexState;
      if (index === 0 && currentPage > 1) {
        setActiveChartIndexState({
          ...activeChartIndexState,
          currentPage: currentPage - 1,
          index: LIMIT - 1 // Go to last item of previous page
        });
      } else {
        // Standard decrement
        setActiveChartIndexState({
          ...activeChartIndexState,
          index: index - 1,
        });
      }
    }
  };

  const handleNextChart = () => {
    if (!showNextOrPrev.hasNext) {
      setCurrentView(parentViewName);
      return;
    }

    if (isFromHarvestRun) {
      setActiveChartIndexState((prev: { index: number }) => ({
        ...prev,
        index: prev.index + 1,
      }));
    } else {
      const LIMIT = 15;
      const { index, currentPage } = activeChartIndexState;
      if (index === LIMIT - 1) {
        setActiveChartIndexState({
          ...activeChartIndexState,
          currentPage: currentPage + 1,
          index: 0
        });
      }
      else {
        setActiveChartIndexState({
          ...activeChartIndexState,
          index: index + 1
        });
      }
    }
  };
  const filteredData = useMemo(() => {
    return goalsData.filter(goal => {
      if (filters.section !== 'all' && goal.section !== filters.section) return false;
      if (filters.action !== 'all' && goal.action !== filters.action) return false;
      if (filters.decision !== 'all' && goal.decision !== filters.decision) return false;
      if (filters.category !== 'all' && goal.category !== filters.category) return false;

      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        return (
          goal?.section?.toLowerCase().includes(query) ||
          goal?.goalTask?.full?.toLowerCase().includes(query) ||
          goal.notes.full?.toLowerCase().includes(query) ||
          goal.suggestion.full?.toLowerCase().includes(query) ||
          goal.action?.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [goalsData, filters]);

  // Calculate pagination
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filters, itemsPerPage]);

  const handleRowClick = (goal: any, event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('input[type="checkbox"]') || target.closest('button')) {
      return;
    }

    // Toggle expansion for this specific row
    setGoalsData(prev => prev.map((g: any, i) =>
      g.i === goal.i ? { ...g, isExpanded: !g.isExpanded } : g
    ));
  };

  const handleExpandAll = () => {
    const newExpandAll = !expandAll;
    setExpandAll(newExpandAll);
    setGoalsData(prev => prev.map(g => ({ ...g, isExpanded: newExpandAll })));
  };
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Create a Set of all indices on the current page as strings
      const newSelection = new Set<string>();
      chartData.forEach((_: any, index: number) => {
        newSelection.add(String(index));
      });
      setSelectedItems(newSelection);
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (index: string, checked: boolean) => {
    const newSelection = new Set(selectedItems);
    if (checked) {
      newSelection.add(index);
    } else {
      newSelection.delete(index);
    }
    setSelectedItems(newSelection);
  }


  const handleSelectedItems = (i?: any, checked?: boolean, totalItems?: number) => {
    if (isAllExpanded) {
      setCollapsedItems((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(i)) {
          newSet.delete(i);
        } else {
          newSet.add(i);
        }


        if (newSet.size === totalItems) {
          setIsAllExpanded(false);
          setCollapsedItems(new Set());
        }

        return newSet;
      });
    } else {

      setSelectedId((prev: any) => (prev === i ? null : i));
    }
    // setIsAllExpanded(false);
    // setSelectedId((prev: any) => (prev === i ? null : i));

  }

  const handleExpandAlls = () => {
    setSelectedId(null);
    setCollapsedItems(new Set());
    setIsAllExpanded(true);
  };

  const handleCollapseAlls = () => {
    setSelectedId(null);
    setCollapsedItems(new Set());
    setIsAllExpanded(false);
  };

  const handleGoalAction = (goal: any, action: 'approve' | 'reject' | 'flag', event: React.MouseEvent) => {
    event.stopPropagation();

    const actionMap = {
      approve: 'Approved',
      reject: 'Rejected',
      flag: 'Flagged for Review'
    };

    if (action === 'reject' || action === 'flag') {
      setPendingAction({
        goalId: goal.section,
        action: actionMap[action],
        comment: ''
      });
      setActionComment('');
      setShowActionDialog(true);
    } else {
      toast.success(`Goal ${goal.section} ${actionMap[action].toLowerCase()} successfully`);
    }
  };

  const handleAuditAction = (action: 'approve' | 'reject') => {
    setPendingAuditAction(action);
    setAuditActionComment('');
    setShowAuditActionDialog(true);
  };

  const confirmAuditAction = async () => {
    if (!pendingAuditAction) return;

    const newStatus: AuditStatus = {
      status: pendingAuditAction === 'approve' ? 'approved' : 'rejected',
      approvedBy: currentUser.name,
      approvedByRole: currentUser.role,
      approvedAt: new Date(),
      comment: auditActionComment.trim() || undefined
    };

    const newDataToSend = {
      outcome: pendingAuditAction === 'approve' ? 'Approved' : 'Rejected',
      supervisorComment: auditActionComment.trim() || ""

    }

    console.log(newDataToSend, "newDataToSend")

    if (!selectedAuditData) return;
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}audits/${selectedAuditData?.id}`, {

        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newDataToSend)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }
      // const newData = await generateMockHarvestData(data)
      setHarvestRuns(data?.data?.audit)
      setChartData(data?.data?.audit?.ai_metadata?.content?.goalUnits)
      fetchData()
      // setCurrentView('harvest-run-detail')
      handleNextChart();
      return data;
    } catch (error: any) {
      console.error('Fetch error:', error.message);
      return null;
    }


    // setAuditStatus(newStatus);

    // toast.success(
    //   `Audit ${pendingAuditAction === 'approve' ? 'approved' : 'rejected'} successfully`,
    //   {
    //     description: `Action completed by ${currentUser.name} on ${new Date().toLocaleDateString()}`,
    //   }
    // );

    // setShowAuditActionDialog(false);
    // setPendingAuditAction(null);
    // setAuditActionComment('');
  };

  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const [chart, setChart] = useState<RNChartRecord | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [reviewedSections, setReviewedSections] = useState<Set<string>>(new Set());

  // Filter sections based on search
  const sections = chart?.chartData?.sections ?? [];

  const filteredSections = searchQuery
    ? sections.filter(s =>
      s.section_title.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : sections;



  // Toggle section as reviewed
  const toggleSectionReview = (sectionNumber: number) => {
    const key = `section-${sectionNumber}`;
    setReviewedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Toggle section expansion
  const toggleSectionExpansion = (sectionNumber: number) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionNumber)) {
        newSet.delete(sectionNumber);
      } else {
        newSet.add(sectionNumber);
      }
      return newSet;
    });
  };

  // Render different widget types
  const renderWidget = (widget: Widget, sectionNumber: number) => {
    switch (widget.widget_type) {
      case 'field':
        if (widget.datatype === 'checkbox') {
          return (
            <div key={widget.field_name} className="bg-white rounded-lg border border-gray-200 px-[8px] py-[4px]">
              {/* <div className="text-gray-500 mb-1" style={{ fontSize: '10px' }}>{widget.label}</div> */}
              <div className="flex items-center gap-2">
                {widget?.value === 'true' ? (
                  // Ticked Checkbox Visual
                  <div className="flex items-center gap-1 text-gray-900  px-1.5 py-1.5 ">
                    <CheckSquare className="w-3 h-3 text-gray-600" />
                    <span style={{ fontSize: '12px', fontWeight: 500 }}>{widget.label}</span>
                  </div>
                ) : (
                  // Unticked Checkbox Visual
                  <div className="flex items-center gap-1 text-gray-900  px-1.5 py-1.5 ">
                    <Square className="w-3 h-3  text-gray-600" />
                    <span style={{ fontSize: '12px' }}>{widget.label}</span>
                  </div>
                )}
              </div>
            </div>
          );
        }
        return (
          <div key={widget.field_name} className="bg-white rounded-lg border border-gray-200 px-[8px] py-[4px]">
            <div className="text-gray-500 mb-0.5" style={{ fontSize: '10px' }}>{widget.label}</div>
            <div className="text-gray-900" style={{ fontSize: '12px' }}>{widget.value ?? ''}</div>
          </div>
        );

      case 'group':
        return (
          <div key={widget.group_name} className="space-y-2">
            <h4 className="text-gray-900" style={{ fontSize: '11px' }}>{widget.group_name}</h4>
            <div className="grid grid-cols-3 gap-2">
              {widget.fields?.map((field: any) => (
                <div key={field.field_name} className="bg-white rounded-lg border border-gray-200 p-2">
                  <div className="text-gray-500 mb-0.5" style={{ fontSize: '10px' }}>{field.label}</div>
                  <div className="text-gray-900" style={{ fontSize: '12px' }}>{field.value ?? ''}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'table':
        // NOTE: The new API data structure does not include tables, but we retain 
        // the rendering logic from the original mock data just in case it's added back.
        return (
          <div key={widget.label} className="space-y-1">
            {widget.label && <h4 className="text-gray-900" style={{ fontSize: '11px' }}>{widget.label}</h4>}
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    {widget.columns?.map((col: any) => (
                      <TableHead key={col.field_name} style={{ fontSize: '10px' }}>{col.label}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {widget.rows?.map((row: any, idx: number) => (
                    <TableRow key={idx}>
                      {widget.columns?.map((col: any) => (
                        <TableCell key={col.field_name} style={{ fontSize: '10px' }}>{row[col.field_name] ?? ''}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        );

      case 'note':
        // NOTE: The new API data structure does not include 'note' type widgets,
        // but this logic is retained from the original component.
        return (
          <div key={`note-${sectionNumber}`} className="bg-blue-50 border border-blue-200 rounded-lg p-2">
            <div className="flex items-start gap-2">
              <Info className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-gray-700" style={{ fontSize: '10px' }}>{widget.note_text}</div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Render section content
  // --- 1. Update renderSection to use Table Rows instead of Divs ---
  const renderSection = (section: Section) => {
    const isReviewed = reviewedSections.has(`section-${section.section_number}`);
    const isExpanded = expandedSections.has(section.section_number);

    const hasOnlyFields = section.widgets.length > 0 && section.widgets.every(w => w.widget_type === "field");
    // Render whatever adjudication the AI returned — do not override based on not_applicable.
    const isSectionApproved = section.adjudication === "SectionIsApproved";
    const isNotApplicable = section.not_applicable === true;

    return (
      <>
        {/* Main Section Row */}
        <TableRow
          key={`section-row-${section.section_number}`}
          className="border-b cursor-pointer hover:bg-gray-50"
          onClick={() => toggleSectionExpansion(section.section_number)}
        >
          {/* COLUMN 1 — Section Content (70%) */}
          <TableCell className="w-[70%] p-0 align-top border-r whitespace-normal break-words">
            <div className="bg-gray-50 px-3 py-2 flex items-center justify-between border-b">
              {/* Left */}
              <div className="flex items-center gap-2">
                <button
                  className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSectionExpansion(section.section_number);
                  }}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-600" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  )}
                </button>

                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 text-[12px]">
                  {section.section_number}
                </div>

                <h3 className="text-gray-900 text-[13px]">{section.section_title}</h3>
              </div>

              {/* Right */}
              <div className="flex items-center gap-2">
                {isNotApplicable ? (
                  <Badge
                    variant="secondary"
                    className="bg-gray-100 text-gray-600 text-[11px] px-2 py-0.5"
                  >
                    N/A
                  </Badge>
                ) : (
                  <p className="text-gray-500 text-[11px]">{section.widgets.length} items</p>
                )}

                {isReviewed && (
                  <Badge
                    variant="default"
                    className="bg-green-100 text-green-700 text-[11px] px-2 py-0.5"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Reviewed
                  </Badge>
                )}
              </div>
            </div>

            {/* Expanded content */}
            {isExpanded && (
              <div className="p-3">
                {isNotApplicable ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <p className="text-gray-500 text-sm italic">This section is not applicable.</p>
                    </div>
                  </div>
                ) : section.widgets.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <p className="text-gray-400 text-sm">No data available for this section.</p>
                    </div>
                  </div>
                ) : hasOnlyFields ? (
                  <div className="grid grid-cols-3 gap-2">
                    {section.widgets.map(widget =>
                      renderWidget(widget, section.section_number)
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {section.widgets.map(widget =>
                      renderWidget(widget, section.section_number)
                    )}
                  </div>
                )}
              </div>
            )}
          </TableCell>

          {/* COLUMN 2 — Adjudication (15%) */}
          <TableCell className={`w-[15%] text-center border-r ${isSectionApproved ? 'bg-green-50/30' : 'bg-red-50/30'}`}>
              <Badge
                className={`${isSectionApproved ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  } text-[11px] px-2 py-0.5`}
              >
                {isSectionApproved ? "Approved" : "Review"}
              </Badge>
          </TableCell>

          {/* COLUMN 3 — Note To Clinician (15%) */}
          <TableCell className="w-[15%] text-center bg-gray-50/30">
            {section.noteToClinician ? (
              <span className="text-gray-700 text-[11px]">{section.noteToClinician}</span>
            ) : (
              <span className="text-gray-400 text-[11px]">—</span>
            )}
          </TableCell>
        </TableRow>
      </>
    );
  };

  const handleBulkAction = (action: 'approve' | 'reject') => {
    if (selectedItems.size === 0) {
      toast.error('Please select items to perform bulk action');
      return;
    }
    setBulkAction(action);
    setBulkDialog(true);
  };

  const confirmBulkAction = () => {
    if (!bulkAction || selectedItems.size === 0) return;

    const actionText = bulkAction === 'approve' ? 'approved' : 'rejected';
    toast.success(`${selectedItems.size} goals ${actionText} successfully`);

    setBulkDialog(false);
    setBulkAction(null);
    setSelectedItems(new Set());
  };

  const confirmAction = () => {
    if (!pendingAction) return;

    toast.success(`Goal ${pendingAction.goalId} ${pendingAction.action.toLowerCase()} successfully`);

    setShowActionDialog(false);
    setPendingAction(null);
    setActionComment('');
  };

  // Updated decision colors - only Compliant and Non-Compliant
  const getDecisionColor = (decision: string) => {
    switch (decision) {
      case 'Compliant': return 'bg-green-100 text-green-800 border-green-200';
      case 'Deficient': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Non-Compliant': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Updated action colors - blue for Performed, yellow for Declined
  const getActionColor = (action: string) => {
    switch (action) {
      case 'Performed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Declined': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 98) return 'text-green-600 bg-green-50';
    if (confidence >= 95) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getUniqueSections = () => {
    return Array.from(new Set(goalsData.map(goal => goal.section))).sort();
  };

  const getUniqueActions = () => {
    return Array.from(new Set(goalsData.map(goal => goal.action))).sort();
  };

  const getUniqueCategories = () => {
    return Array.from(new Set(goalsData.map(goal => goal.category))).sort();
  };

  // Updated stats calculations for new decision values
  const getGoalsStats = () => {
    const compliant = filteredData.filter(goal => goal.decision === 'Compliant').length;
    const nonCompliant = filteredData.filter(goal => goal.decision === 'Non-Compliant').length;
    const needsAction = filteredData.filter(goal => goal.needsAction).length;
    const performed = filteredData.filter(goal => goal.action === 'Performed').length;
    const avgConfidence = filteredData.length > 0
      ? (filteredData.reduce((sum, goal) => sum + goal.confidenceScore, 0) / filteredData.length).toFixed(1)
      : '0';

    return { compliant, nonCompliant, needsAction, performed, avgConfidence, total: filteredData.length };
  };

  const stats = getGoalsStats();

  function removeDuplicateSentences(input?: string): string {
    if (!input || typeof input !== 'string') return '';

    const seen = new Set<string>();

    return input
      .split(/[.;]+/)
      .map(sentence => sentence.trim())
      .filter(sentence => {
        const normalized = sentence.replace(/\s+/g, ' ');
        if (!normalized || seen.has(normalized)) return false;
        seen.add(normalized);
        return true;
      })
      .join('. ') + '.';
  }


  // Check if any filters are active
  const hasActiveFilters = filters.searchQuery || filters.section !== 'all' || filters.action !== 'all' || filters.decision !== 'all' || filters.category !== 'all';

  if (!selectedAuditData) {
    return null; // Will redirect via useEffect
  }
  const handleUndoAuditAction = async () => {

    try {
      const response = await apiClient(`audits/${selectedUndoCharts}`, {
        method: 'PUT',
        token,
        body: {
          outcome: "Pending",
          supervisorComment: ""
        }
      }


      )

      const data = await response.json();
      if (data?.success) {
        toast.success('Undo Decision successfully');
      }

    }
    catch (error: any) {
      console.error('Error fetching categories:', error.message);
    }
    finally {
      fetchData();
      setSelectedUndoCharts(null);
    }
  }

  return (
    <Layout>
      <div ref={scrollRef} id="main-scroll-area" className="min-h-screen">
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white">
          <div className="p-8">
            {/* Breadcrumb Navigation */}
            <div className="mb-6">
              <Breadcrumb>
                <BreadcrumbList>
                  {isFromHarvestRun && (
                    <>
                      <BreadcrumbItem>
                        <BreadcrumbLink href="#" onClick={() => {
                          setCurrentView('chat-harvest');
                          navigate(ROUTES.chartReviewList)
                        }} className="flex items-center gap-2">
                          {/* <FileSearch className="w-3 h-3" /> */}
                          Chart Review

                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator />
                    </>
                  )}
                  <BreadcrumbItem>
                    <BreadcrumbLink
                      href="#"
                      onClick={() => {
                        setCurrentView(parentViewName);
                        isFromHarvestRun ? navigate(-1) : navigate(ROUTES.clientOverviewList);
                      }}
                      className="flex items-center gap-2"
                    >
                      {isFromHarvestRun ? 'Chart Review Details' : 'Client Overview'}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>Chart Details</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            {/* Sticky Header */}
            <div className="sticky top-0 z-50 bg-gradient-to-br from-gray-50 via-blue-50/30 to-white/95 backdrop-blur-sm border-b border-gray-200/50 mb-8 -mx-8 px-8 py-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCurrentView(parentViewName);
                      isFromHarvestRun ? navigate(-1) : navigate(ROUTES.clientOverviewList);
                    }}
                    className="flex items-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {isFromHarvestRun ? 'Back to Chart Review Details' : 'Back to Client Overview'}
                  </Button>
                  <div>
                    <h1 className="text-gray-900 mb-2 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text">
                      Chart Details {isAideChart === "AIDE" ? "- AIDE"
                        : isAideChart === "SN" ? "- SN" : (() => {
                          const group = harvestRuns?.ai_metadata?.content?.goalUnits?.[0]?.group ||
                            harvestRuns?.chart_data?.goalUnits?.[0]?.group;
                          return group ? `- ${group}` : "";
                        })()}
                    </h1>
                    {/* <p className="text-gray-600">
                    Detailed review of all goals for {selectedAuditData.clientName} visit on {new Date(harvestRuns?.updated_at).toLocaleDateString()}
                  </p> */}
                  </div>
                </div>

                {/* {showScrollText && (
                  <div className='flex items-center gap-14 mx-2'>
                    {(showNextOrPrev.hasPrev || showNextOrPrev.hasNext ) && 
  <div>{showNextOrPrev.hasPrev ?<Button   className='bg-gray-400 hover:bg-gray-500 text-white' onClick={handlePreviousChart}>{showNextOrPrev.hasPrev ? "Previous Chart" : "Back to List"}</Button>:null}</div>
 }
                  <div className="text-sm text-blue-800 font-medium">
                    Overall Chart Review Decision : {harvestRuns?.ai_metadata?.content?.OverallAuditDecision || harvestRuns?.ai_metadata?.content?.aiDecision}
                  </div>
{(showNextOrPrev.hasPrev || showNextOrPrev.hasNext ) && <div>{showNextOrPrev.hasNext?<Button   className='bg-gray-400 hover:bg-gray-500 text-white' onClick={handleNextChart}>{showNextOrPrev.hasNext ? "Next Chart" : "Back to List"}</Button>:null}</div>}
                  </div>
                )} */}

                <div className="flex items-center gap-3">
                  {/* Previous/Next Navigation (Disabled for single chart view) */}
                  <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 p-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                      onClick={handlePreviousChart}
                      disabled={!showNextOrPrev.hasPrev || auditDataloadng} // Disable as only one chart is loaded
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <Separator orientation="vertical" className="h-6" />
                    <span className="text-sm text-gray-500 px-2">
                      Chart {isFromHarvestRun
                        ? activeChartIndexState.index + 1
                        : (((activeChartIndexState.currentPage || passedCurrentchartIndex.currentPage) - 1) * 15) + (activeChartIndexState.index || passedCurrentchartIndex.index) + 1
                      } of {totalCount || 1}
                    </span>
                    <Separator orientation="vertical" className="h-6" />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                      onClick={handleNextChart}
                      disabled={!showNextOrPrev.hasNext || auditDataloadng} // Disable as only one chart is loaded
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>

                  {hasPermission("audits:review") && !harvestRuns?.kt_update && harvestRuns?.outcome !== "Rejected" && harvestRuns?.outcome !== "Approved" && (
                    <>
                      <Button
                        onClick={() => handleAuditAction('approve')}
                        className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Agree with AI
                      </Button>
                      <Button
                        onClick={() => handleAuditAction('reject')}
                        variant="destructive"
                        className="flex items-center gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                        Disagree with AI
                      </Button>
                    </>
                  )}
                  {hasPermission("audits:review") && !harvestRuns?.kt_update && (harvestRuns?.outcome === "Approved" || harvestRuns?.outcome === "Rejected") ? (
                    <Button
                      onClick={() => setSelectedUndoCharts(harvestRuns?.id)}
                      className="bg-gray-500 hover:bg-gray-600 text-white flex items-center gap-2"
                    >
                      <Undo className="w-4 h-4" />
                      Undo Decision
                    </Button>
                  ) : null

                  }
                </div>
                {/* <div className="flex items-center gap-3"> */}
                {/* {hasPermission("audits:review") && harvestRuns?.outcome !== "Rejected" && harvestRuns?.outcome !== "Approved" && (
                    <>
                      <Button
                        onClick={() => handleAuditAction('approve')}
                        className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Agree with AI
                      </Button>
                      <Button
                        onClick={() => handleAuditAction('reject')}
                        variant="destructive"
                        className="flex items-center gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                        Disagree with AI
                      </Button>
                    </>
                  )}
                  {hasPermission("audits:review") && (harvestRuns?.outcome === "Approved" || harvestRuns?.outcome === "Rejected") ? (
                    <Button
                        onClick={() => setSelectedUndoCharts(harvestRuns?.id)}
                        className="bg-gray-500 hover:bg-gray-600 text-white flex items-center gap-2"
                      >
                        <Undo className="w-4 h-4" />
                        Undo Decision
                      </Button>
                  ):null

                  } */}

                {/* <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Export as PDF
                </Button>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg">
                  <Target className="w-6 h-6 text-blue-600" />
                </div> */}
                {/* </div> */}
              </div>
            </div>
            {/* {(showNextOrPrev.hasPrev || showNextOrPrev.hasNext ) && <div className='flex justify-end gap-6 my-4'>
  {showNextOrPrev.hasPrev ?<div><Button   className='bg-gray-400 hover:bg-gray-500 text-white' onClick={handlePreviousChart}>{showNextOrPrev.hasPrev ? "Previous Chart" : "Back to List"}</Button></div>:null}
  {showNextOrPrev.hasNext?<div><Button   className='bg-gray-400 hover:bg-gray-500 text-white' onClick={handleNextChart}>{showNextOrPrev.hasNext ? "Next Chart" : "Back to List"}</Button></div>:null}
  </div>} */}
            {/* Audit Status Banner */}
            {(harvestRuns?.outcome === "Rejected" || harvestRuns?.outcome === "Approved") && (
              <Card className={`mb-6 border-2 ${harvestRuns?.outcome === 'Approved'
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
                }`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-1 rounded-full ${harvestRuns?.outcome === 'Approved'
                        ? 'bg-green-100'
                        : 'bg-red-100'
                        }`}>
                        {harvestRuns?.outcome === 'Approved' ? (
                          <CheckCircle2 className="w-6 h-6 text-green-600" />
                        ) : (
                          <XCircle className="w-6 h-6 text-red-600" />
                        )}
                      </div>
                      <div>
                        <h3 className={`font-medium ${harvestRuns?.outcome === 'Approved' ? 'text-green-900' : 'text-red-900'
                          }`}>
                          {harvestRuns?.outcome === 'Approved' ? 'Agree with AI' : 'Disagree with AI'}

                        </h3>
                        <div className="flex items-center gap-4 mt-1">
                          <div className='flex flex-col gap-y-1'>
                            <div className="flex items-center gap-2">
                              {/* <UserCheck className={`w-4 h-4 ${harvestRuns?.outcome === 'Approved' ? 'text-green-600' : 'text-red-600'
                            }`} /> */}
                              <span className={`text-sm ${harvestRuns?.outcome === 'Approved' ? 'text-green-700' : 'text-red-700'
                                }`}>
                                {"Auditor Name"} : {harvestRuns?.auditor_name}
                                {/* {auditStatus.approvedByRole} */}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-sm ${harvestRuns?.outcome === 'Approved' ? 'text-green-700' : 'text-red-700'
                                }`}>
                                Date : {new Date(harvestRuns?.approved_reject_date)?.toLocaleDateString('en-US')}  Time : {new Date(harvestRuns?.approved_reject_date)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', })}
                              </span>
                            </div>
                            {/* <div className="flex items-center gap-2">
                              <span className={`text-sm ${harvestRuns?.outcome === 'Approved' ? 'text-green-700' : 'text-red-700'
                                }`}>
                                Time : {new Date(harvestRuns?.approved_reject_date)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', })}
                              </span>
                            </div> */}

                            <div className="flex items-center gap-2">
                              <span className={`text-sm ${harvestRuns?.outcome === 'Approved' ? 'text-green-700' : 'text-red-700'
                                }`}>
                                Comments to Aukera Health : {harvestRuns?.supervisor_comment?.trim() || "None"}
                              </span>
                            </div>
                            {/* <div className="flex items-center gap-2">
                            <span className={`text-sm ${harvestRuns?.outcome === 'Approved' ? 'text-green-700' : 'text-red-700'
                              }`}>
                              Supervisor :
                            </span>
                            <span className={`text-sm ${harvestRuns?.outcome === 'Approved' ? 'text-green-700' : 'text-red-700'
                              }`}>
                              {harvestRuns?.supervisor_name} 
                            </span>
                          </div> */}
                          </div>
                          {/* <div className="flex items-center gap-2">
                          <CalendarIcon className={`w-4 h-4 ${harvestRuns?.outcome === 'Approved' ? 'text-green-600' : 'text-red-600'
                            }`} />
                          <span className={`text-sm ${harvestRuns?.outcome === 'Approved' ? 'text-green-700' : 'text-red-700'
                            }`}>
                            {new Date(harvestRuns?.approved_reject_date)?.toLocaleString()}
                          </span>
                        </div> */}
                        </div>
                        {/* {harvestRuns?.outcome && (
                        <p className={`text-sm mt-2 ${harvestRuns?.outcome === 'Approved' ? 'text-green-700' : 'text-red-700'
                          }`}>
                          "{harvestRuns?.supervisor_comment}"
                        </p>
                      )} */}
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-sm ${harvestRuns?.outcome === 'Approved'
                        ? 'bg-green-100 text-green-800 border-green-300'
                        : 'bg-red-100 text-red-800 border-red-300'
                        }`}
                    >
                      {harvestRuns?.outcome === 'Approved' ? 'AGREED' : 'DISAGREED'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}



            {/* Content Section */}

            <Card className={` border-2 bg-blue-50 border-blue-200 mb-4`}>
              <CardContent className="p-6">
                {/* <div className="flex items-center justify-between"> */}
                {/* <div className='grid grid-cols-2 gap-4'> */}
                {/* <div className="flex items-center gap-4">
                    <CheckCircle2 className="w-6 h-6 text-blue-600" />

                    <p className='text-blue-600 '><span className='font-semibold text-blue-800'>Overall Audit Feedback</span> : {harvestRuns?.ai_metadata?.content?.overallAuditFeedback}</p>
                  </div> */}
                <div className="">
                  <div className='flex items-center gap-y-3 gap-4'>
                    <div className='mb-auto p-1 rounded-full'><CheckCircle2 className="w-6 h-6 text-blue-600" /></div>
                    <div className='gap-y-3'>
                      <div className='font-semibold text-blue-800 gap-2'>Overall Chart Review Decision : <span className='font-normal'>{harvestRuns?.ai_metadata?.content?.OverallAuditDecision || harvestRuns?.ai_metadata?.content?.aiDecision}</span></div>
                      {harvestRuns?.ai_metadata?.content?.OverallAuditDecision === "Send for Correction" && <>
                        <div className='font-semibold text-blue-800'>Supervisor Name : <span className='font-normal'>{harvestRuns.supervisor_name}</span></div>
                        {/* <div>
                            <div className='font-semibold text-blue-800'>AI's Note to Clinician: <span className='font-normal'>{removeDuplicateSentences(harvestRuns.ai_metadata?.content?.ConcatenatedNoteToClinician)}</span></div>
                          </div> */}
                      </>
                      }
                      {harvestRuns?.ai_metadata?.content?.OverallAuditDecision === "Send for Correction" && (
                        <div className="flex items-start gap-2">
                          <div className="font-semibold text-blue-800">
                            Note to Clinician:{" "}
                            <span className="font-normal">
                              {removeDuplicateSentences(
                                harvestRuns.ai_metadata?.content?.auditorNotesToClinician ||
                                harvestRuns.ai_metadata?.content?.SummaryOfNoteToClinician ||
                                harvestRuns.ai_metadata?.content?.ConcatenatedNoteToClinician ||
                                harvestRuns.ai_metadata?.content?.concatenatedNoteToClinician)}
                            </span>
                          </div>
                          {/* Edit Icon */}
                          {!harvestRuns?.kt_update && (
                            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                              <DialogTrigger asChild>
                                <Edit
                                  className="w-4 h-4 text-blue-600 cursor-pointer m-1"
                                />
                              </DialogTrigger>
                              {/* Popup Content */}
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Edit Note to Clinician</DialogTitle>
                                </DialogHeader>
                                <textarea
                                  ref={textareaRef}
                                  value={editNoteValue}
                                  onChange={(e) => setEditNoteValue(e.target.value)}
                                  className="min-h-[100px] w-full rounded-md border bg-white border-gray-300 p-3 text-sm shadow-sm focus:border-gray-500 focus:ring-1 focus:ring-gray-500 outline-none resize-y"
                                  placeholder="Enter note details..." />
                                <DialogFooter>
                                  <Button
                                    variant="secondary"
                                    onClick={() => setEditNoteValue("")}
                                    className="mr-auto">
                                    Clear
                                  </Button>
                                  <DialogClose asChild>
                                    <Button variant="outline">Cancel</Button>
                                  </DialogClose>
                                  <Button
                                    disabled={isSavingNote}
                                    onClick={() => {
                                      console.log("Send to API:", editNoteValue);
                                      updateNoteApi(editNoteValue, selectedAuditData?.id);
                                    }}>Save
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>)}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* <p className='text-blue-600 ml-[40px] '> </p> */}
                </div>

                {/* {isAdmin && <div className="">
                    <div className='flex items-center gap-2 mb-2'>
                      <CheckCircle2 className="w-6 h-6 text-blue-600" />
                      <div className='font-semibold text-blue-800'>AI's Note to Clinician: {harvestRuns?.ai_metadata?.content?.aiDecision}</div>
                    </div>
                    // <p className='text-blue-600 ml-[40px] '> </p>
                  </div>} */}
                {/* </div> */}

              </CardContent>
            </Card>

            <div className="mb-8">
              {/* Audit Summary Card */}
              <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg mb-6">
                {isAdmin && <>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      Prompt & Model Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-gray-600 text-sm">Prompt Name</p>
                        <p className="font-medium">{harvestRuns?.prompt_name}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Prompt Version</p>
                        <p className="font-medium">{harvestRuns?.prompt_version}</p>

                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Model</p>
                        <p className="font-medium">{harvestRuns?.model_type || harvestRuns?.ai_metadata?.model}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Provider</p>
                        <p className="font-medium">{harvestRuns?.ai_metadata?.provider}</p>

                      </div>
                    </div>
                  </CardContent>
                  <hr />
                </>}
                {/* <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Audit Summary
                </CardTitle>
              </CardHeader> */}
                <CardContent className={`${!isAdmin ? "pt-6" : ""}`}>
                  {/* <div className={isAdmin ? "grid grid-cols-2 md:grid-cols-5 gap-4 py-2" : "grid grid-cols-2 md:grid-cols-9 gap-4 py-2"}> */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 py-2">
                    <div>
                      <p className="text-gray-600 text-sm">Client Id</p>
                      {harvestRuns?.client_id}
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Client</p>
                      <p className="font-medium">{harvestRuns?.client_name}</p>
                      <p className="text-sm text-gray-500">{selectedAuditData.clientId}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Clinician</p>
                      <p className="font-medium">{harvestRuns?.clinician_name}</p>
                      <p className="text-sm text-gray-500">{selectedAuditData.department}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Visit Date</p>
                      <p className="font-medium">
                        {/* {new Date(selectedAuditData.visitDate).toLocaleDateString()} */}
                        {/* - */}
                        {/* {new Date(harvestRuns?.chart_data?.visit?.visitDate ?? "").toLocaleDateString('en-US')} */}
                        {harvestRuns?.chart_data?.visit?.visitDate ?? ""}

                      </p>
                      {/* <p className="text-sm text-gray-500">{selectedAuditData.visitReason}</p> */}
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Submitted Date</p>
                      <p className="font-medium">
                        {harvestRuns?.ai_metadata?.visit?.submittedOn ??
                          harvestRuns?.ai_metadata?.content?.visit?.submittedOn ??
                          ""}
                      </p>
                    </div>
                    {/* <div>
                    <p className="text-gray-600 text-sm">Auditor</p>
                    <p className="font-medium">{selectedAuditData.auditorName}</p>
                    <p className="text-sm text-gray-500">Review: {selectedAuditData.reviewTime}h</p>
                  </div> */}

                    {/* //  <div>
                    //   <p className="text-gray-600 text-sm">Confidence</p>
                    //   <div className="flex items-center gap-2">
                    //     <div className={`w-3 h-3 rounded-full ${selectedAuditData.confidence_score >= 90 ? 'bg-green-500' :
                    //       selectedAuditData.confidence_score >= 75 ? 'bg-yellow-500' : 'bg-red-500'
                    //       }`} />
                    //     <span className="font-medium">{harvestRuns.confidence_score}%</span>
                    //   </div>
                    // </div> */}
                    <div>
                      <p className="text-gray-600 text-sm">Check In</p>
                      <p className="font-medium">
                        {/* {new Date(harvestRuns?.chart_data?.visit?.checkInTime ?? "").toLocaleDateString('en-US')} */}
                        {harvestRuns?.chart_data?.visit?.checkInTime}
                      </p>
                    </div>

                    <div>
                      <p className="text-gray-600 text-sm">Check Out</p>
                      <p className="font-medium">{harvestRuns?.chart_data?.visit?.checkOutTime} </p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Hours Spent</p>
                      < p className="font-medium">{harvestRuns?.chart_data?.visit?.hours} </p>
                    </div>
                    {isAideChart != "SN" && (
                      <div>
                        <p className="text-gray-600 text-sm">Total Goals</p>
                        <p className="font-medium">{harvestRuns?.ai_metadata?.content?.goalUnits?.length}</p>
                        {/* <p className="text-sm text-gray-500">{stats.compliant} compliant</p> */}
                      </div>)}
                    <div>
                      <p className="text-gray-600 text-sm">{`${orgConfig?.name} Status`}</p>
                      <p className="font-medium">{harvestRuns?.kt_update}</p>
                      {/* <p className="text-sm text-gray-500">{selectedAuditData.kt_update}</p> */}
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">{`${orgConfig?.name} Update Date/Time`}</p>
                      <p className="font-medium">
                        {harvestRuns?.kt_update_time ? (<>
                          {new Date(harvestRuns.kt_update_time).toLocaleDateString('en-US')}{" "}
                          {new Date(harvestRuns.kt_update_time).toLocaleTimeString('en-US', { hour: "2-digit", minute: "2-digit", })}
                        </>
                        ) : null}
                      </p>
                      {/* <p className="text-sm text-gray-500">{selectedAuditData.kt_update}</p> */}
                    </div>

                    {/* {isAdmin && <div>
                      <p className="text-gray-600 text-sm">Total Goals</p>
                      <p className="font-medium">{harvestRuns?.ai_metadata?.content?.goalUnits?.length}</p>
                      
                    </div>} */}
                  </div>
                  {isAideChart === "SN" && harvestRuns?.chart_data?.visit?.clientAlert && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-gray-600 text-sm mb-2">Client Alert</p>
                      <p className="font-medium text-sm leading-relaxed break-words">
                        {harvestRuns.chart_data.visit.clientAlert}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>



              {/* Updated Goals Stats */}
              {/* <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">{stats.compliant}</p>
                      <p className="text-gray-600 text-sm">Compliant</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-red-100 p-2 rounded-lg">
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">{stats.nonCompliant}</p>
                      <p className="text-gray-600 text-sm">Non-Compliant</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <Activity className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">{stats.performed}</p>
                      <p className="text-gray-600 text-sm">Performed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-orange-100 p-2 rounded-lg">
                      <Flag className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">{stats.needsAction}</p>
                      <p className="text-gray-600 text-sm">Needs Action</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-purple-100 p-2 rounded-lg">
                      <Target className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">{stats.avgConfidence}%</p>
                      <p className="text-gray-600 text-sm">Avg AI Score</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div> */}
            </div>
            {isAideChart === "SN" && (
              <div className="mb-2 flex items-center justify-end gap-2 me-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const allSectionNumbers = new Set(chart?.chartData.sections.map(s => s.section_number));
                    setExpandedSections(allSectionNumbers);
                  }}
                  className="text-xs"
                >
                  <ChevronDown className="w-3 h-3 mr-1" />
                  Expand All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setExpandedSections(new Set())}
                  className="text-xs"
                >
                  <ChevronRight className="w-3 h-3 mr-1" />
                  Collapse All
                </Button>
              </div>
            )}
            {/* Enhanced Data Table with Integrated Filters */}
            <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg overflow-hidden">
              {selectedItems.size > 0 && (<CardHeader className="border-b border-gray-100">
                {/* <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div>
                    <CardTitle>Healthcare Audit Goals Detail</CardTitle>
                    <CardDescription>
                      {totalItems} total goals • {selectedItems.size} selected • Click any row to expand/collapse
                    </CardDescription>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExpandAll}
                >
                  {expandAll ? <ChevronDown className="w-4 h-4 mr-2" /> : <ChevronRight className="w-4 h-4 mr-2" />}
                  {expandAll ? 'Collapse All' : 'Expand All'}
                </Button>
              </div> */}

                {/* Integrated Filters and Search */}
                <div className="space-y-4">
                  {/* <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                  
                  <div className="relative md:col-span-2">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search goals..."
                      value={filters.searchQuery}
                      onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                      className="pl-9"
                    />
                  </div>

                  
                  <Select value={filters.section} onValueChange={(value) => setFilters(prev => ({ ...prev, section: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Sections" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sections</SelectItem>
                      {getUniqueSections().map((section) => (
                        <SelectItem key={section} value={section}>
                          {section}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={filters.action} onValueChange={(value) => setFilters(prev => ({ ...prev, action: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Actions</SelectItem>
                      {getUniqueActions().map((action) => (
                        <SelectItem key={action} value={action}>
                          {action}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={filters.decision} onValueChange={(value) => setFilters(prev => ({ ...prev, decision: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Decisions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Decisions</SelectItem>
                      <SelectItem value="Compliant">Compliant</SelectItem>
                      <SelectItem value="Non-Compliant">Non-Compliant</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filters.category} onValueChange={(value) => setFilters(prev => ({ ...prev, category: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {getUniqueCategories().map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div> */}

                  {/* Filter Status and Clear Option */}
                  {/* <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    
                    {hasActiveFilters && (
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="flex items-center gap-1">
                          <Filter className="w-3 h-3" />
                          {Object.values(filters).filter(v => v && v !== 'all').length} active
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setFilters({
                            section: 'all',
                            action: 'all',
                            decision: 'all',
                            category: 'all',
                            searchQuery: '',
                          })}
                        >
                          Clear All
                        </Button>
                      </div>
                    )}
                  </div>
                </div> */}

                  {/* Bulk Actions */}
                  {/* {selectedItems.size > 0 && ( */}
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <span className="text-green-800 font-medium">
                      {selectedItems.size} goal{selectedItems.size !== 1 ? 's' : ''} selected
                    </span>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleBulkAction('approve')}
                    >
                      <ThumbsUp className="w-4 h-4 mr-2" />
                      Bulk Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleBulkAction('reject')}
                    >
                      <ThumbsDown className="w-4 h-4 mr-2" />
                      Bulk Reject
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedItems(new Set())}
                    >
                      Clear Selection
                    </Button>
                  </div>
                  {/* )} */}
                </div>
              </CardHeader>
              )}

              <CardContent className="p-0">
                <div className="w-full">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead colSpan={isAideChart === 'SN' ? 1 : 5} className="text-center text-sm font-semibold text-gray-800 bg-gray-100 border-b ">
                          <div className='flex justify-center items-center gap-x-2'>
                            <div className='mt-1'>EXTRACTED FROM</div>
                            <div>{orgConfig?.logo ? <img src={orgConfig?.logo} alt={`${orgConfig?.name} Logo`} className="w-26 h-8" /> : <p className='mt-1 uppercase'>{orgConfig?.name}</p>}</div>
                          </div>
                        </TableHead>
                        <TableHead colSpan={4} className={`text-center text-sm font-semibold text-gray-800 bg-gray-100 ${isAideChart === 'SN' ? 'border-l-2  border-gray-120' : 'border-l-4  border-gray-300'}  `}>
                          <div className='flex justify-center items-center gap-x-2'>
                            <div>AI GENERATED</div>
                            <div className='mb-1'><img src={AI} alt="Example" className="w-26 h-4" /></div>
                          </div>

                        </TableHead>
                      </TableRow>
                      <TableRow className="bg-gray-50 hover:bg-gray-50">
                        {isAideChart === 'SN' ? (
                          // --- CUSTOM HEADERS FOR SN CHART ---
                          <>
                            <TableHead className="font-inter font-semibold text-[14px] text-[#758195] w-[70%] pl-6 uppercase">
                              Section
                            </TableHead>

                            {/* Adjudication Header - 15% */}
                            <TableHead className={`font-inter font-semibold text-[14px] text-[#758195] w-[15%]  ${isAideChart === 'SN' ? 'border-l-2  border-gray-120' : 'border-l-4  border-gray-300'} text-center uppercase`}>
                              Adjudication
                            </TableHead>

                            {/* Note Header - 15% */}
                            <TableHead className="font-inter font-semibold text-[14px] text-[#758195] w-[15%] text-center uppercase">
                              Note To Clinician
                            </TableHead>
                          </>
                        ) : (
                          <>

                            {(isAideChart) && <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Group</TableHead>}
                            <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Personal Care Task (PCT)</TableHead>
                            <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195] w-24">Action</TableHead>
                            <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195] w-20 ">Reason Code</TableHead>
                            <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195] w-56 ">Notes</TableHead>
                            <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195] w-24 border-l-4 border-gray-300 pl-3"> Adjudication  </TableHead>
                            <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195] w-56 ">Note To Clinician</TableHead>
                            {/* <TableHead className="font-medium text-gray-700 w-56">AI Suggestion</TableHead> */}
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={isAideChart === 'SN' ? 3 : 9} className="text-center py-12">
                            <div className="flex flex-col items-center gap-2">
                              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                                <Search className="w-6 h-6 text-gray-400" />
                              </div>
                              <p className="text-gray-500">No goals found</p>
                              <p className="text-gray-400 text-sm">Try adjusting your filters</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        isAideChart === 'SN' ? (
                          // ========= SN CHART LOGIC (SECTIONS) =========
                          filteredSections?.length > 0 ? (
                            filteredSections.map(section => renderSection(section))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={9} className="text-center py-12">
                                <div className="flex flex-col items-center gap-2">
                                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                                    <Search className="w-6 h-6 text-gray-400" />
                                  </div>
                                  <p className="text-gray-500">No sections match your search query</p>
                                </div>
                              </TableCell>
                            </TableRow>
                          )
                        ) : (
                          chartData?.map((goal: any, i: any) => {
                            // const isOpen = isAllExpanded ? !collapsedItems.has(i) : selectedId === i;
                            const isOpen = true
                            return (<React.Fragment key={i}>
                              {/* Main Row */}
                              <TableRow
                                className={`text-gray-600 hover:bg-blue-50/50 transition-colors cursor-pointer ${goal.decision === 'Non-Compliant' ? 'bg-red-50/30' : ''
                                  }`}
                                // onClick={(event) => handleRowClick(goal, event)}
                                onClick={() => handleSelectedItems(i, checked, chartData?.length)}
                              >
                                {/* <TableCell onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={selectedItems.has(String(i))}
                                  onCheckedChange={(checked) => handleSelectItem(String(i), !!checked)}



                                />
                              </TableCell> */}


                                {/* Section */}

                                {/* <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <div>
                                  {isOpen ? (
                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-400" />
                                  )}
                                </div>

                              </div>
                            </TableCell> */}
                                {/* <TableCell className=" text-gray-600 font-medium w-[6rem] break-all whitespace-normal">
                                {harvestRuns?.client_id}
                              </TableCell> */}
                                {/* <TableCell className=" text-gray-600 font-medium w-[6rem] break-all whitespace-normal">
                                {goal?.goalTask?.itemCode || goal?.itemCode  }
                              </TableCell> */}

                                {(isAideChart) && <TableCell className="">
                                  {/* <div className="w-50">  */}
                                  {/* <p className="text-sm text-gray-600 leading-relaxed break-words whitespace-normal"> */}
                                  {goal?.group}
                                  {/* </p> */}
                                  {/* </div> */}
                                </TableCell>}

                                {/* Goal Task */}
                                <TableCell className="">
                                  <div className="w-full">
                                    <p className="text-sm text-gray-600 leading-relaxed break-words whitespace-normal">
                                      {selectedId === i ? goal?.goalTask?.description || goal?.description : goal?.goalTask?.description || goal?.description}
                                    </p>
                                    {!goal?.isExpanded && goal?.goalTask?.full?.length > goal?.goalTask?.collapsed?.length && (
                                      <p className="text-xs text-blue-600 mt-1">Click to expand</p>
                                    )}
                                  </div>
                                </TableCell>

                                {/* Action */}
                                <TableCell>
                                  {/* <Badge variant="outline" className={`${getActionColor(goal?.action)} text-xs`}>
                                {goal?.goalTask?.action}
                              </Badge> */}
                                  {goal?.goalTask?.action || goal?.action}
                                </TableCell>

                                {/* Reason Code */}
                                <TableCell>
                                  {/* {goal?.reasonCode && (
                                <code className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                  {goal?.goalTask?.reasonCode}
                                  -
                                </code>
                              )} */}
                                  {goal?.goalTask?.reasonCode || goal?.reasonCode}
                                </TableCell>

                                {/* Notes */}
                                <TableCell className="max-w-56">
                                  <div className="w-full">
                                    <p className="text-sm  break-words whitespace-normal">
                                      {goal?.isExpanded ? goal?.goalTask?.notes || goal?.notes || goal?.note : goal?.goalTask?.notes || goal?.notes || goal?.note}
                                    </p>
                                  </div>
                                </TableCell>

                                {/* Decision */}
                                <TableCell className='border-l-4 border-gray-300  pl-3'>
                                  {/* <Badge variant="outline" className={`${getDecisionColor(goal?.goalTask?.notesReview?.decision)} text-xs`}>
                                {goal?.goalTask?.NoteEvaluation}
                              </Badge> */}
                                  {goal?.goalTask?.Adjudication || goal?.Adjudication}
                                </TableCell>

                                {/* AI Score */}
                                <TableCell>
                                  <div className="flex items-center gap-2 ">
                                    {/* <div className={`w-2 h-2 rounded-full ${goal?.goalTask?.notesReview?.score >= 98 ? 'bg-green-500' :
                                  goal?.goalTask?.notesReview?.score >= 95 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`} />
                                <span className={`text-sm font-medium px-2 py-1 rounded ${getConfidenceColor(goal?.goalTask?.notesReview?.score)}`}>
                                  {goal?.goalTask?.notesReview?.score}%
                                </span> */}
                                    <p className="break-words whitespace-normal ">
                                      {goal?.goalTask?.NoteToClinician || goal?.NoteToClinician}
                                    </p>
                                  </div>
                                </TableCell>

                                {/* AI Suggestion */}
                                {/* <TableCell className="max-w-56">
                              <div className="w-full">
                                <p className="text-sm text-gray-900 break-words whitespace-normal">
                                  {goal?.isExpanded ? goal?.goalTask?.notesReview?.suggestion : goal?.goalTask?.notesReview?.suggestion}
                                </p>
                              </div>
                            </TableCell> */}
                              </TableRow>

                              {/* Expanded Sub-Goal Rows */}
                              {isOpen && goal?.subItems?.map((subGoal: any, index: number) => (
                                <TableRow key={`sub-${index}`} className="hover:bg-blue-50/50 transition-colors text-gray-600">
                                  {/* <TableCell></TableCell> */}
                                  {/* <TableCell> </TableCell> */}
                                  {/* Empty Section for sub-goals */}
                                  {/* <TableCell className="font-medium w-[6rem] break-all whitespace-normal ">
                                 
                                </TableCell> */}
                                  <TableCell className="font-medium w-[6rem] break-all whitespace-normal ">
                                    {subGoal?.itemCode}
                                  </TableCell>
                                  {/* <TableCell className="max-w-80 pl-6">
                                <div className="w-full">
                                  <p className="text-sm text-gray-800 break-words whitespace-normal">
                                    {subGoal?.itemCode}
                                  </p>
                                </div>
                              </TableCell> */}

                                  {/* Sub-Goal Task */}
                                  <TableCell className="">
                                    <div className="w-full">
                                      <p className="text-sm  break-words whitespace-normal">
                                        {subGoal?.description}
                                      </p>
                                    </div>
                                  </TableCell>

                                  {/* Sub-Goal Action */}
                                  <TableCell>
                                    {/* <Badge variant="outline" className={`${getActionColor(subGoal?.action)} text-xs`}>
                                  {subGoal?.action}
                                </Badge> */}
                                    {subGoal?.action}
                                  </TableCell>

                                  {/* Sub-Goal Reason Code */}
                                  <TableCell>
                                    {/* {subGoal?.reasonCode && (
                                  <code className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                    {subGoal?.reasonCode}
                                  </code>
                                )} */}
                                    {subGoal?.reasonCode}
                                  </TableCell>

                                  {/* Sub-Goal Notes */}
                                  <TableCell className="">
                                    <div className="w-full">
                                      <p className="text-sm  break-words whitespace-normal">
                                        {subGoal?.notes}
                                      </p>
                                    </div>
                                  </TableCell>

                                  {/* Sub-Goal Decision */}
                                  <TableCell className='border-l-4 border-gray-300  pl-3'>
                                    {/* <Badge variant="outline" className={`${getDecisionColor(subGoal?.notesReview?.decision)} text-xs`}>
                                  {subGoal?.notesReview?.decision}
                                </Badge> */}
                                    {subGoal?.Adjudication}
                                  </TableCell>

                                  {/* Sub-Goal AI Score */}
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      {/* <div className={`w-2 h-2 rounded-full ${subGoal?.notesReview?.score >= 98 ? 'bg-green-500' :
                                    subGoal?.notesReview?.score >= 95 ? 'bg-yellow-500' : 'bg-red-500'
                                    }`} />
                                  <span className={`text-sm font-medium px-2 py-1 rounded ${getConfidenceColor(subGoal?.notesReview?.score)}`}>
                                    {subGoal?.notesReview?.score}%
                                  </span> */}
                                      <p className="break-words whitespace-normal ">
                                        {subGoal?.NoteToClinician}
                                      </p>
                                    </div>
                                  </TableCell>

                                  {/* Sub-Goal AI Suggestion */}
                                  {/* <TableCell className="max-w-56">
                                <div className="w-full">
                                  <p className="text-sm text-gray-800 break-words whitespace-normal">
                                    {subGoal?.notesReview?.suggestion}
                                  </p>
                                </div>
                              </TableCell> */}
                                </TableRow>
                              ))}
                            </React.Fragment>)
                          })
                        )
                      )}
                    </TableBody>
                  </Table>

                </div>


                {/* Enhanced Pagination */}
                {totalItems > 0 && (
                  <div className="p-6 border-t border-gray-100">
                    <PaginationCustom
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={totalItems}
                      itemsPerPage={itemsPerPage}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={(newSize) => {
                        setItemsPerPage(newSize);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
            <div className="my-8">
              {/* Audit Summary Card */}
              <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg mb-6">
                {/* <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    
                  </CardTitle>
                </CardHeader> */}
                <CardContent className='p-5'>
                  <p className='text-gray-600 '><span className=' mb-1 text-black font-semibold'>Shift Narrative :</span>  {harvestRuns?.ai_metadata?.content?.visit?.sectionShiftNarrative} </p>

                  <div>
                    {isAdmin && <p className='font-semibold py-4'>Shift Narrative Review</p>}
                    {isAdmin && <div className='grid grid-cols-2 gap-4'>
                      <span className='text-gray-600'><span className='text-black font-semibold'>Decision :</span>  {harvestRuns?.ai_metadata?.content?.visit?.sectionShiftNarrativeReview?.decision}  {harvestRuns?.ai_metadata?.content?.visit?.shiftNarrativeReview?.decision} </span>
                      <span className='text-gray-600'> <span className='text-black font-semibold'>Score</span> : {harvestRuns?.ai_metadata?.content?.visit?.sectionShiftNarrativeReview?.score}  {harvestRuns?.ai_metadata?.content?.visit?.shiftNarrativeReview?.score}</span>

                    </div>}
                    <p className='text-gray-600 my-3'><span className='text-black font-semibold'>Note to Clinician</span> : {harvestRuns?.ai_metadata?.content?.NoteToClinician}</p>
                  </div>
                </CardContent>
              </Card>


            </div>
            <div className="my-8">
              {/* Audit Summary Card */}
              <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg mb-6">
                {/* <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    
                  </CardTitle>
                </CardHeader> */}
                <CardContent className='p-5'>
                  <span className='font-semibold'>Today's Notes : </span>
                  {harvestRuns?.ai_metadata?.content?.visit?.todaysNotes || "-"}
                  {isAdmin && <div>
                    <p className='font-semibold py-4'>Notes Review</p>
                    <div className='grid grid-cols-2 gap-4'>
                      <span className='text-gray-600'><span className='text-black font-semibold'>Decision :</span>  {harvestRuns?.ai_metadata?.content?.visit?.todaysNotesReview?.decision}</span>
                      <span className='text-gray-600'> <span className='text-black font-semibold'>Score</span> : {harvestRuns?.ai_metadata?.content?.visit?.todaysNotesReview?.score}</span>

                    </div>
                    <p className='text-gray-600 py-3'><span className='text-black font-semibold'>Suggestion</span> : {harvestRuns?.ai_metadata?.content?.visit?.todaysNotesReview?.suggestion}</p>
                  </div>}
                </CardContent>
              </Card>


            </div>

            <div className="my-8">
              {/* Audit Summary Card */}
              <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg mb-6">
                {/* <CardHeader>
                <CardTitle className="flex items-center ">
                 
                </CardTitle>
              </CardHeader> */}
                <CardContent className='p-5'>
                  <div className='grid grid-cols-2 gap-4'>
                    <p className='flex flex-col gap-y-2'>
                      <p className='text-gray-600'><span className='font-semibold text-black'>Clinician Signature Present</span> : <span className="font-medium">
                        {(() => {
                          const value = harvestRuns?.chart_data?.visit?.clinicianSignature?.toLowerCase().trim();

                          return !value || value === 'not signed' || value === 'notsigned'
                            ? 'No'
                            : 'Yes';
                        })()}
                      </span></p>
                      {/* <p className='text-gray-600'><span className='font-semibold text-black'>Clinician Signature</span> : {harvestRuns?.chart_data?.visit?.clinicianSignature}</p> */}
                      <p className="text-gray-600">
                        <span className="font-semibold text-black">Clinician Signature Date</span> :{" "}
                        {(() => {
                          const rawString = harvestRuns?.chart_data?.visit?.clinicianSignature;

                          if (!rawString) return 'N/A';

                          // Extract the date part using RegExp
                          const dateMatch = rawString.match(/\b\d{2}\/\d{2}\/\d{4}\b/);
                          if (!dateMatch) return 'Invalid date';

                          const date = new Date(dateMatch[0]);
                          const isValid = !isNaN(date.getTime());

                          return isValid ? date.toLocaleDateString('en-US') : 'Invalid date';
                        })()}
                      </p>

                      {/* <p className='text-gray-600'><span className='font-semibold text-black'>Clinician Signed On Date </span> : {harvestRuns?.chart_data?.visit?.clientSignedOnDate}</p> */}
                      {/* <p className='text-gray-600'><span className='font-semibold text-black'>Clinician Signed On Time </span> : {harvestRuns?.chart_data?.visit?.clientSignedOnTime}</p> */}

                    </p>
                    <p className='flex flex-col gap-y-2'>
                      <p className='text-gray-600'><span className='font-semibold text-black'>Client Signature Present</span> : {harvestRuns?.chart_data?.visit?.signaturePresent}</p>
                      {/* <p className='text-gray-600'><span className='font-semibold text-black'>Client Signature</span> : {harvestRuns?.chart_data?.visit?.clinicianSignature}</p> */}
                      <p className='text-gray-600'><span className='font-semibold text-black'>Client Signature Date </span> : {harvestRuns?.chart_data?.visit?.clientSignedOnDate}</p>
                      <p className='text-gray-600'><span className='font-semibold text-black'>Client Signature Time </span> : {harvestRuns?.chart_data?.visit?.clientSignedOnTime}</p>

                    </p>
                    {/* <p className='text-gray-600'><span className='font-semibold text-black'>Signature</span> : {harvestRuns?.chart_data?.visit?.signaturePresent}</p> */}
                    {/* <p className='text-gray-600'><span className='font-semibold text-black'>Overall Audit Feedback</span> : {harvestRuns?.ai_metadata?.content?.overallAuditFeedback}</p> */}
                  </div>
                </CardContent>
              </Card>


            </div>

            {/* Dialogs */}
            <AlertDialog open={showBulkDialog} onOpenChange={setBulkDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm Bulk Action</AlertDialogTitle>
                  <AlertDialogDescription>
                    You are about to {bulkAction} {selectedItems.size} goal{selectedItems.size !== 1 ? 's' : ''}.
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={confirmBulkAction}>
                    Confirm {bulkAction === 'approve' ? 'Approval' : 'Rejection'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showActionDialog} onOpenChange={setShowActionDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm Goal Action</AlertDialogTitle>
                  <AlertDialogDescription>
                    {pendingAction && (
                      `You are about to ${pendingAction.action.toLowerCase()} goal ${pendingAction.goalId}. Please provide a comment:`
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="py-4">
                  <Textarea
                    value={actionComment}
                    onChange={(e) => setActionComment(e.target.value)}
                    placeholder="Explain your decision..."
                    rows={3}
                  />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={confirmAction}
                    disabled={!actionComment.trim()}
                  >
                    Confirm {pendingAction?.action}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* Audit Action Dialog */}
            <AlertDialog open={showAuditActionDialog} onOpenChange={setShowAuditActionDialog}>
              <AlertDialogContent className="max-w-xl sm:max-w-2xl h-[70vh] p-0 flex flex-col">

                {/* Header */}
                <AlertDialogHeader className="p-6 border-b">
                  <AlertDialogTitle>
                    {pendingAuditAction === 'approve' ? 'Agree Chart Review' : 'Disagree Chart Review'}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {/* You are about to {pendingAuditAction} this audit for{" "} */}
                    You are about to to  {pendingAuditAction === 'approve' ? 'agree' : 'disagree'} with AI for this eChart
                    <strong>{selectedAuditData?.patientName}</strong>.
                    {pendingAuditAction === 'reject' && ' Please provide a reason for rejection.'}
                  </AlertDialogDescription>
                </AlertDialogHeader>

                {/* Scrollable Body with expanding Textarea */}
                <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
                  <Textarea
                    value={auditActionComment}
                    onChange={(e) => setAuditActionComment(e.target.value)}
                    placeholder={
                      pendingAuditAction === 'approve'
                        ? 'Optional comment...'
                        : 'Reason for rejection (required)...'
                    }
                    className="flex-1 resize-none min-h-[100px] focus:min-h-[200px]"
                  />
                  <div className="p-3 bg-gray-50 rounded-lg text-sm">
                    <p className="font-medium text-gray-800 mb-2">Action will be recorded as:</p>
                    <p className="text-gray-600">
                      {pendingAuditAction === 'approve' ? 'Agreed' : 'Disagreed'} by -{' '}
                      {user?.name + ' ' + user?.lastName}
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <AlertDialogFooter className="bg-gradient-to-br from-gray-50 via-blue-50/30 to-white/95 backdrop-blur-sm px-6 py-4 border-t">
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={confirmAuditAction}
                    disabled={pendingAuditAction === 'reject' && !auditActionComment.trim()}
                    className={pendingAuditAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    {pendingAuditAction === 'approve' ? 'I Agree with AI' : 'I Disagree with AI'}
                  </AlertDialogAction>
                </AlertDialogFooter>

              </AlertDialogContent>
            </AlertDialog>



            <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Goal Details - {selectedGoal?.section}</DialogTitle>
                  <DialogDescription>
                    Comprehensive view of goal information and audit findings
                  </DialogDescription>
                </DialogHeader>

                {selectedGoal && (
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Target className="w-4 h-4" />
                          {selectedGoal?.section} - Healthcare Goal Detail
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <p className="text-gray-600 text-sm mb-2">Goal Task</p>
                          <p className="text-gray-900">{selectedGoal?.goalTask.full}</p>
                        </div>

                        <Separator />

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-gray-600 text-sm mb-2">Action Taken</p>
                            <Badge variant="outline" className={getActionColor(selectedGoal?.action)}>
                              {selectedGoal.action}
                            </Badge>
                          </div>
                          <div>
                            <p className="text-gray-600 text-sm mb-2">Decision</p>
                            <Badge variant="outline" className={getDecisionColor(selectedGoal.decision)}>
                              {selectedGoal.decision}
                            </Badge>
                          </div>
                        </div>

                        <Separator />

                        <div>
                          <p className="text-gray-600 text-sm mb-2">Detailed Notes</p>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-gray-900">{selectedGoal.notes.full}</p>
                          </div>
                        </div>

                        <div>
                          <p className="text-gray-600 text-sm mb-2">AI Suggestion</p>
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <p className="text-blue-900">{selectedGoal.suggestion.full}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-gray-600 text-sm mb-2">AI Score</p>
                            <p className="font-medium">{selectedGoal.confidenceScore}%</p>
                          </div>
                          <div>
                            <p className="text-gray-600 text-sm mb-2">Category</p>
                            <p className="font-medium">{selectedGoal.category}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={selectedUndoCharts} onOpenChange={() => setSelectedUndoCharts(null)}>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Client ID: {harvestRuns?.client_id}</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to undo the decision of this chart?
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSelectedUndoCharts(null)}>
                    Close
                  </Button>
                  <Button variant="approve" onClick={handleUndoAuditAction}>
                    {/* <Undo className="w-4 h-4" /> */}
                    Undo
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </Layout>
  );
}